import { Link } from 'react-router';
import { useTranslation } from '../store/useStore';
import { Shield, Mail, MapPin, Phone } from 'lucide-react';

export default function Footer() {
  const { t } = useTranslation();

  const footerLinks = [
    {
      title: t('footer.about'),
      links: [
        { label: 'About IPO', href: '#' },
        { label: 'Our Story', href: '#' },
        { label: 'Careers', href: '#' },
        { label: 'Press & Media', href: '#' },
        { label: 'Blog', href: '#' },
      ],
    },
    {
      title: t('footer.suppliers'),
      links: [
        { label: 'Become a Supplier', href: '/supplier/register' },
        { label: 'Supplier Dashboard', href: '/dashboard' },
        { label: 'Upload a Deal', href: '/dashboard' },
        { label: 'Verification Process', href: '#' },
        { label: 'Supplier Points', href: '#' },
      ],
    },
    {
      title: t('footer.ipme'),
      links: [
        { label: 'Apply as IPME', href: '/ipme' },
        { label: 'IPME Dashboard', href: '/dashboard' },
        { label: 'Deal Recommendations', href: '/recommend' },
        { label: 'Commission Structure', href: '#' },
        { label: 'Training Resources', href: '#' },
      ],
    },
    {
      title: t('footer.support'),
      links: [
        { label: 'Help Center', href: '#' },
        { label: 'Dispute Resolution', href: '#' },
        { label: 'Escrow Protection', href: '#' },
        { label: 'Security', href: '#' },
        { label: 'Privacy Policy', href: '#' },
      ],
    },
  ];

  return (
    <footer className="bg-[#0f172a] text-white">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-1 font-display">Stay Updated with IPO</h3>
              <p className="text-sm text-white/60">Get the latest deals, supplier updates, and marketplace news delivered to your inbox.</p>
            </div>
            <div className="flex gap-3 w-full lg:w-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 lg:w-72 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder:text-white/40 focus:border-[#c2410c] focus:ring-1 focus:ring-[#c2410c] outline-none"
              />
              <button className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-medium whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-hero rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-display">IPO</span>
            </div>
            <p className="text-sm text-white/60 mb-6 max-w-sm">
              Instant Purchase Online — Your trusted global marketplace connecting verified suppliers with buyers worldwide. Secure escrow, AI-powered matching, and comprehensive multilingual support.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-white/60">
                <MapPin className="w-4 h-4 shrink-0" />
                <span>1 Raffles Place, Singapore 048616</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-white/60">
                <Mail className="w-4 h-4 shrink-0" />
                <span>support@ipo.com</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-white/60">
                <Phone className="w-4 h-4 shrink-0" />
                <span>+65 6123 4567</span>
              </div>
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="text-sm font-semibold mb-4 uppercase tracking-wider text-white/80">{section.title}</h4>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-white/50 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">
              &copy; {new Date().getFullYear()} Instant Purchase Online (IPO). All rights reserved. Registered in Singapore.
            </p>
            <div className="flex items-center gap-6">
              <Link to="#" className="text-xs text-white/40 hover:text-white transition-colors">Terms of Service</Link>
              <Link to="#" className="text-xs text-white/40 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="#" className="text-xs text-white/40 hover:text-white transition-colors">Cookie Policy</Link>
              <Link to="#" className="text-xs text-white/40 hover:text-white transition-colors">GDPR</Link>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 text-center">
            <p className="text-[10px] text-white/30">
              All payments are processed through licensed third-party escrow providers. IPO does not hold buyer funds directly. 
              Your transaction is protected by our escrow guarantee. | Secure SSL Encryption | PCI DSS Compliant
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
